/**
 * Frontend human-in-the-loop tool for multiple-choice quiz questions.
 *
 * Registered via makeAssistantTool so assistant-ui:
 *   1. Sends the tool definition in RunAgentInput.tools (making it available
 *      to the LLM via the Pydantic AI adapter's _AGUIFrontendToolset)
 *   2. Recognizes the tool call as a frontend "human" tool, setting
 *      status to "requires-action" instead of marking it terminal
 *   3. Enables addResult() to send the user's answer back to the agent
 */
import { useState, useMemo } from "react";
import { makeAssistantTool } from "@assistant-ui/react";
import type { ToolCallMessagePartComponent } from "@assistant-ui/react";
import { Loader2 } from "lucide-react";
import { cn } from "~/lib/utils";

type FrontendQuizArgs = {
  question: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  correct_answer: string;
};

const LABELS = ["A", "B", "C", "D"] as const;

const FrontendQuizRender: ToolCallMessagePartComponent<FrontendQuizArgs, string> = ({
  args,
  result,
  status,
  addResult,
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const parsed = useMemo(() => {
    if (args == null) return null;
    if (typeof args.question === "string") return args;
    return null;
  }, [args]);

  const isRunning = status.type === "running";
  const hasAnswered = selectedIndex !== null || result != null;

  if (isRunning || !parsed) {
    return (
      <div className="border border-gray-200 rounded-lg p-4 my-2 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        <span>Generating question...</span>
      </div>
    );
  }

  const options = [parsed.option_a, parsed.option_b, parsed.option_c, parsed.option_d];

  if (result != null) {
    const match = typeof result === "string" ? result.match(/selected: ([A-D])/) : null;
    // Use capture group [1] to extract the letter after "selected: "
    const selectedLabel = match ? (match[1] ?? match[0]).toUpperCase() : null;
    return (
      <div className="border-2 border-purple-200 rounded-lg p-4 my-2 space-y-3">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
            Frontend Tool
          </span>
        </div>
        <p className="font-medium text-sm">{parsed.question}</p>
        <div className="flex flex-col gap-2">
          {options.map((option: string, i: number) => {
            if (!option) return null;
            const label = LABELS[i];
            const isSelected = selectedLabel === label;
            return (
              <div
                key={label}
                className={cn(
                  "flex items-center text-left text-sm rounded-lg border px-3 py-2.5",
                  isSelected
                    ? "border-purple-600 bg-purple-600 text-white font-medium"
                    : "border-gray-100 bg-gray-50/50 text-gray-300"
                )}
              >
                <span
                  className={cn(
                    "font-semibold mr-2.5",
                    isSelected ? "text-purple-200" : "text-gray-300"
                  )}
                >
                  {label}.
                </span>
                {option}
              </div>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground">Answer submitted</p>
      </div>
    );
  }

  const handleSelect = (index: number) => {
    if (hasAnswered) return;
    setSelectedIndex(index);
    addResult(
      `The user selected: ${LABELS[index]}. Evaluate whether this matches correct_answer and respond with text only. Do NOT call any tool.`
    );
  };

  return (
    <div className="border-2 border-purple-200 rounded-lg p-4 my-2 space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
          Frontend Tool
        </span>
      </div>
      <p className="font-medium text-sm">{parsed.question}</p>
      <div className="flex flex-col gap-2">
        {options.map((option: string, i: number) => {
          if (!option) return null;
          const label = LABELS[i];
          const isSelected = selectedIndex === i;
          return (
            <button
              key={label}
              type="button"
              disabled={hasAnswered}
              onClick={() => handleSelect(i)}
              className={cn(
                "flex items-center text-left text-sm rounded-lg border px-3 py-2.5 transition-all duration-150",
                isSelected
                  ? "border-purple-500 bg-purple-100 text-purple-800 font-medium"
                  : hasAnswered
                    ? "border-gray-200 bg-gray-50 text-gray-400 cursor-default"
                    : "border-purple-200 bg-purple-50/60 text-gray-800 hover:bg-purple-100 hover:border-purple-300 active:scale-[0.98] cursor-pointer"
              )}
            >
              <span className="font-semibold text-purple-600 mr-2.5">{label}.</span>
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const FrontendQuizTool = makeAssistantTool<FrontendQuizArgs, string>({
  toolName: "ask_frontend_multiple_choice",
  type: "human",
  description:
    "Present a multiple-choice question to the user. " +
    "Generate a question, four options (A-D), and the correct answer letter.",
  parameters: {
    type: "object",
    properties: {
      question: { type: "string", description: "The question text" },
      option_a: { type: "string", description: "Option A text" },
      option_b: { type: "string", description: "Option B text" },
      option_c: { type: "string", description: "Option C text" },
      option_d: { type: "string", description: "Option D text" },
      correct_answer: {
        type: "string",
        description: "The correct option letter (A, B, C, or D)",
      },
    },
    required: ["question", "option_a", "option_b", "option_c", "option_d", "correct_answer"],
  },
  render: FrontendQuizRender,
});
